import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * 把 reorder 时对 prompt（mentions）的重写推迟到下一帧再落地，避免和 dnd-kit 的 drop 过渡
 * 动画抢同一帧导致拖放后"甩一下再归位"。
 *
 * 同时解决两个边角问题：
 * 1) **unmount 清理**：rAF 还没触发就卸载时主动 cancel，避免 React 警告 / 写入已销毁节点。
 * 2) **快速连续重排的竞态**：16ms 内连续两次重排，第二次重写如果直接以 `localText` 为基线，
 *    会得到错误结果（因为第一次的 setLocalText 还没生效，而 oldInputs 已经反映了第一次的
 *    idx 编号）。这里用 `pendingRef` 保存"rAF 之后即将落地的文本"作为下次重写的基线。
 */
export function useDeferredInstructionsRewrite(
  localText: string,
  setLocalText: (v: string) => void,
  commitToStore: (text: string) => void,
) {
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<string | null>(null);
  // 调度 rAF 时记录"我们预期 rAF 触发时 localText 应该是什么"，
  // 用来在 rAF 真正触发时检测用户是否在中途自己改动了文本。
  const expectedLocalAtRafRef = useRef<string | null>(null);
  // 用 ref 镜像最新的 localText，使 getBaselineText 自身保持稳定引用
  // （否则 localText 每次变更都会让消费者的 useCallback 重建，引发不必要的重渲染级联）。
  const localTextRef = useRef(localText);
  localTextRef.current = localText;

  useEffect(
    () => () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingRef.current = null;
      expectedLocalAtRafRef.current = null;
    },
    [],
  );

  /** 取得当前应作为 rewrite 基线的文本（优先用尚未落地的 pending，其次才是 React state）。 */
  const getBaselineText = useCallback(
    () => pendingRef.current ?? localTextRef.current,
    [],
  );

  const cancelPending = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingRef.current = null;
    expectedLocalAtRafRef.current = null;
  }, []);

  /** 排程一帧后再落地新文本；若已有未触发的 rAF 会被取消，由本次接力。 */
  const schedule = useCallback(
    (nextText: string) => {
      pendingRef.current = nextText;
      // 期望 rAF 触发时 localText 还是当前值；若用户在 16ms 内修改了，则放弃本次写入
      // 以免覆盖用户输入。
      expectedLocalAtRafRef.current = localTextRef.current;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const txt = pendingRef.current;
        const expected = expectedLocalAtRafRef.current;
        pendingRef.current = null;
        expectedLocalAtRafRef.current = null;
        if (txt == null) return;
        if (expected !== null && localTextRef.current !== expected) return;
        setLocalText(txt);
        commitToStore(txt);
      });
    },
    [setLocalText, commitToStore],
  );

  /**
   * 立刻同步落地新文本，并取消任何尚未触发的 rAF（被本次显式提交取代）。
   * 用于非拖放路径（如删除/断开），需要立即给到用户反馈。
   */
  const commitNow = useCallback(
    (nextText: string) => {
      cancelPending();
      setLocalText(nextText);
      commitToStore(nextText);
    },
    [cancelPending, setLocalText, commitToStore],
  );

  return useMemo(
    () => ({ getBaselineText, schedule, commitNow }),
    [getBaselineText, schedule, commitNow],
  );
}
